import { useEffect, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { labelTalentType, labelCreatorType } from "@/lib/roleCategories";
import { useDMSocket } from "@/hooks/useDMSocket";
import { Sparkles, Inbox, Users, UserPlus, Check, X, MessageCircle, MapPin, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "suggestions", label: "Suggestions", icon: Sparkles },
  { id: "requests", label: "Demandes reçues", icon: Inbox },
  { id: "connections", label: "Mes connexions", icon: Users },
  { id: "sent", label: "Envoyées", icon: Send },
];

const typeLabel = (u) => (u.role === "talent" ? labelTalentType(u.type) : labelCreatorType(u.type)) || (u.role === "talent" ? "Talent" : "Créateur");

export default function Network() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "suggestions";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ requests: 0, connections: 0 });

  const load = async (t = tab) => {
    setLoading(true);
    setItems([]);
    try {
      const url = t === "suggestions" ? "/network/suggestions?limit=12" : `/network/connections?status=${t === "connections" ? "accepted" : t}`;
      const [{ data }, c] = await Promise.all([api.get(url), api.get("/network/counts").catch(() => ({ data: counts }))]);
      setItems(Array.isArray(data) ? data : []);
      setCounts(c.data);
    } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  useDMSocket((ev) => { if (ev.type === "connection_request" || ev.type === "connection_accepted") load(); });

  const setTab = (id) => setParams({ tab: id }, { replace: true });

  return (
    <AppShell>
      <div data-testid="network-page">
        <div className="mb-8">
          <p className="caption-label text-[#C68E59]">Mon réseau</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1 leading-tight">Les bonnes <span className="italic text-[#A26F3E]">rencontres</span></h1>
          <p className="text-sm text-[#8B8680] mt-2 max-w-2xl">Connectez-vous aux membres qui complètent votre univers. Une fois la demande acceptée, la messagerie privée s'ouvre.</p>
        </div>
        <div className="flex flex-wrap gap-2 border-b border-[#EADDC9] pb-3 mb-8" data-testid="network-tabs">
          {TABS.map((t) => {
            const active = tab === t.id;
            const count = t.id === "requests" ? counts.requests : t.id === "connections" ? counts.connections : null;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-[#1A1A1A] text-white" : "text-[#6E4B2A] hover:bg-[#FAF3EA]"}`} data-testid={`network-tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
                {count > 0 && <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-semibold ${active ? "bg-[#D4A574] text-[#1A1A1A]" : "bg-[#C68E59] text-white"}`}>{count}</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#C68E59] animate-spin" /></div>
        ) : items.length === 0 ? (
          <Empty tab={tab} />
        ) : tab === "suggestions" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="suggestions-grid">
            {items.filter((s) => s && Array.isArray(s.reasons)).map((s) => <SuggestionCard key={s.user_id} s={s} onDone={() => setItems((p) => p.filter((x) => x.user_id !== s.user_id))} />)}
          </div>
        ) : (
          <ul className="bg-white rounded-3xl border border-[#EADDC9] divide-y divide-[#FAF3EA]" data-testid={`${tab}-list`}>
            {items.filter((c) => c && c.user).map((c) => <ConnectionRow key={c.id} c={c} tab={tab} onChange={() => load()} />)}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

function Avatar({ u, size = "w-14 h-14" }) {
  return (
    <div className={`${size} rounded-2xl overflow-hidden bg-[#FAF3EA] shrink-0 relative`}>
      <div className="w-full h-full flex items-center justify-center font-serif text-xl text-[#A26F3E]">{(u.name || "?")[0]}</div>
      {u.picture && <img src={optimizeUrl(u.picture, 320)} alt={u.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      {u.online && <span className="absolute bottom-1 right-1 w-2.5 h-2.5 rounded-full bg-[#2E7D32] border-2 border-white" />}
    </div>
  );
}

function SuggestionCard({ s, onDone }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/network/connections", { user_id: s.user_id });
      toast.success(data.status === "accepted" ? `Vous êtes connecté·e avec ${s.name}` : `Demande envoyée à ${s.name}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); } finally { setBusy(false); }
  };
  return (
    <article className="bg-white rounded-3xl border border-[#EADDC9] p-5 flex flex-col hover:border-[#C68E59]/50 transition-colors" data-testid={`suggestion-${s.user_id}`}>
      <div className="flex items-start gap-4">
        <Link to={s.profile_path}><Avatar u={s} size="w-16 h-16" /></Link>
        <div className="min-w-0 flex-1">
          <Link to={s.profile_path} className="font-serif text-xl text-[#1A1A1A] leading-tight hover:text-[#A26F3E] block truncate">{s.name}</Link>
          <p className="text-xs text-[#6E4B2A] mt-1">{typeLabel(s)}</p>
          {s.location && <p className="text-xs text-[#8B8680] mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</p>}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#C68E59] font-semibold shrink-0">{s.score}% match</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {s.reasons.map((r) => <span key={r} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FAF3EA] text-[#6E4B2A]">{r}</span>)}
      </div>
      <button onClick={connect} disabled={busy} className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#1A1A1A] hover:bg-[#C68E59] text-white text-xs font-semibold h-10 transition-colors disabled:opacity-60" data-testid={`suggestion-connect-${s.user_id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Se connecter
      </button>
    </article>
  );
}

function ConnectionRow({ c, tab, onChange }) {
  const navigate = useNavigate();
  const u = c.user;
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
  };
  const openThread = async () => {
    try { const { data } = await api.post("/dm/threads", { user_id: u.user_id }); navigate(`/messages/${data.id}`); } catch (e) { toast.error(e.response?.data?.detail || "Impossible d'ouvrir la conversation"); }
  };
  return (
    <li className="p-4 sm:p-5 flex items-center gap-4" data-testid={`connection-${c.id}`}>
      <Link to={u.profile_path}><Avatar u={u} /></Link>
      <div className="flex-1 min-w-0">
        <Link to={u.profile_path} className="font-medium text-[#1A1A1A] hover:underline truncate block">{u.name}</Link>
        <p className="text-xs text-[#8B8680] truncate">{typeLabel(u)}{u.location ? ` · ${u.location}` : ""}</p>
        {tab === "requests" && c.message && <p className="text-xs text-[#6E4B2A] mt-1.5 italic line-clamp-2">« {c.message} »</p>}
      </div>
      {tab === "requests" && (
        <div className="flex gap-2 shrink-0">
          <button onClick={() => act(() => api.post(`/network/connections/${c.id}/accept`), "Connexion acceptée")} className="w-9 h-9 rounded-full bg-[#E7F5EC] text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white flex items-center justify-center transition-colors" title="Accepter" data-testid={`accept-${c.id}`}><Check className="w-4 h-4" /></button>
          <button onClick={() => act(() => api.post(`/network/connections/${c.id}/decline`), "Demande refusée")} className="w-9 h-9 rounded-full bg-red-50 text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white flex items-center justify-center transition-colors" title="Refuser" data-testid={`decline-${c.id}`}><X className="w-4 h-4" /></button>
        </div>
      )}
      {tab === "connections" && (
        <div className="flex gap-2 shrink-0">
          <button onClick={openThread} className="inline-flex items-center gap-1.5 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-xs font-semibold px-4 h-9 transition-colors" data-testid={`message-${c.id}`}><MessageCircle className="w-3.5 h-3.5" /> Message</button>
          <button onClick={() => act(() => api.delete(`/network/connections/${c.id}`), "Connexion retirée")} className="w-9 h-9 rounded-full border border-[#EADDC9] text-[#8B8680] hover:border-[#D32F2F] hover:text-[#D32F2F] flex items-center justify-center transition-colors" title="Retirer" data-testid={`remove-${c.id}`}><X className="w-4 h-4" /></button>
        </div>
      )}
      {tab === "sent" && <span className="text-xs text-[#8B8680] shrink-0">En attente</span>}
    </li>
  );
}

function Empty({ tab }) {
  const copy = {
    suggestions: ["Pas encore de suggestion", "Complétez votre profil (ville, univers, photos) pour recevoir des recommandations plus précises."],
    requests: ["Aucune demande en attente", "Les membres qui souhaitent se connecter avec vous apparaîtront ici."],
    connections: ["Aucune connexion pour l'instant", "Parcourez les suggestions pour démarrer votre réseau."],
    sent: ["Aucune demande envoyée", "Vos demandes en attente de réponse apparaîtront ici."],
  }[tab];
  return (
    <div className="rounded-3xl bg-white border border-dashed border-[#EADDC9] p-14 text-center" data-testid="network-empty">
      <Users className="w-10 h-10 text-[#C68E59] mx-auto opacity-60" />
      <p className="font-serif text-xl text-[#1A1A1A] mt-4">{copy[0]}</p>
      <p className="text-sm text-[#8B8680] mt-2 max-w-md mx-auto">{copy[1]}</p>
    </div>
  );
}
